import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { NotesThread } from "./module-widgets";

export default function EntityDrawer({ open, onClose, title, collection, patchBase, fields, item, onSaved, renderExtra }) {
  const [state, setState] = useState(item || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setState(item || {}); }, [item]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    const payload = { ...state };
    fields.forEach((f) => {
      if (f.type === "date" && payload[f.key] && String(payload[f.key]).length <= 10)
        payload[f.key] = new Date(payload[f.key]).toISOString();
      if (f.type === "number" && payload[f.key] != null) payload[f.key] = Number(payload[f.key]);
    });
    try {
      const { data } = await api.patch(`${patchBase}/${item.id}`, payload);
      onSaved?.(data); toast.success("Saved"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  if (!item) return null;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="entity-drawer">
        <SheetHeader><SheetTitle className="font-heading">{title}</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-4">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea rows={3} value={state[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} />
              ) : f.type === "select" ? (
                <Select value={String(state[f.key] ?? "")} onValueChange={(v) => set(f.key, v)}>
                  <SelectTrigger data-testid={`field-${f.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === "switch" ? (
                <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 text-sm">
                  <span className="text-muted-foreground">{state[f.key] ? "Yes" : "No"}</span>
                  <Switch checked={!!state[f.key]} onCheckedChange={(v) => set(f.key, v)} data-testid={`field-${f.key}`} />
                </div>
              ) : f.type === "date" ? (
                <Input type="date" value={(state[f.key] || "").slice(0, 10)} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} />
              ) : (
                <Input type={f.type === "number" ? "number" : "text"} value={state[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} />
              )}
            </div>
          ))}

          {renderExtra && <div className="border-t border-border pt-4">{renderExtra(state, setState)}</div>}

          <Button onClick={save} disabled={busy} className="w-full rounded-full" data-testid="drawer-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Changes</>}
          </Button>

          {collection && (
            <div className="border-t border-border pt-4">
              <NotesThread collection={collection} itemId={item.id} notes={state.notes || []}
                onUpdate={(updated) => { setState(updated); onSaved?.(updated); }} />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
